import { SPLIT_BILL_IMAGE } from "@dineout-payment-success-ui/constants";
import {
    ISplitPayWidget,
    ISplitPayWidgetActiveState,
    SplitMethod,
} from "@dineout-payment-success-ui/interfaces";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { isEmpty } from "@swiggy-private/js-utils";

export const isSplitByAmountDefaultMode = (defaultSplitModeSelection?: string): boolean =>
    defaultSplitModeSelection === SplitMethod.AMOUNT;

export const getCurrentSplitWidgetStateFromApi = (
    splitPayWidget?: ISplitPayWidget,
): string => {
    const key = splitPayWidget?.currentSplitWidgetState;
    if (!isEmpty(key) && splitPayWidget?.states?.[key]) {
        return key;
    }

    // if no state is found, return default
    return "default";
};

export type SplitBillWidgetTitleBadgePlacement = "beforeTitle" | "afterTitle";

export enum SplitBillWidgetTitleBadgeType {
    IMAGE = "image",
    ICON = "icon",
}

export type SplitBillWidgetTitleBadge =
    | {
          type: SplitBillWidgetTitleBadgeType.IMAGE;
          cloudinaryId: string;
          width: string;
          height: string;
          placement: SplitBillWidgetTitleBadgePlacement;
      }
    | {
          type: SplitBillWidgetTitleBadgeType.ICON;
          iconName: IconName;
          fillColor: keyof IColors;
          strokeColor: keyof IColors;
          placement: SplitBillWidgetTitleBadgePlacement;
      };

export interface ISplitBillWidgetCtaConfig {
    text: string;
    iconName: IconName;
    fillColor: keyof IColors;
    strokeColor: keyof IColors;
}

export interface ISplitBillWidgetViewState {
    title: string;
    subTitle: string;
    lottieId: string;
    widgetLottieId: string;
    titleBadge?: SplitBillWidgetTitleBadge;
    cta?: ISplitBillWidgetCtaConfig;
    useSuccessSubtitleWidth: boolean;
    isClickable: boolean;
}

const resolveTitleBadge = (state: ISplitPayWidgetActiveState): SplitBillWidgetTitleBadge | undefined => {
    const stateWithIcon = state as ISplitPayWidgetActiveState & { titleIcon?: string; titleIconColor?: string };

    if (stateWithIcon.titleIcon) {
        const iconColor = (stateWithIcon.titleIconColor as keyof IColors | undefined) || "positive";

        return {
            type: SplitBillWidgetTitleBadgeType.ICON,
            iconName: stateWithIcon.titleIcon as IconName,
            fillColor: iconColor,
            strokeColor: iconColor,
            placement: "beforeTitle",
        };
    }

    if ("leftImageId" in state && state.leftImageId) {
        return {
            type: SplitBillWidgetTitleBadgeType.IMAGE,
            cloudinaryId: state.leftImageId,
            width: SPLIT_BILL_IMAGE.LEFT_LOGO_WIDTH,
            height: SPLIT_BILL_IMAGE.LEFT_LOGO_HEIGHT,
            placement: "afterTitle",
        };
    }

    if ("rightImageId" in state && state.rightImageId) {
        return {
            type: SplitBillWidgetTitleBadgeType.IMAGE,
            cloudinaryId: state.rightImageId,
            width: SPLIT_BILL_IMAGE.RIGHT_LOGO_WIDTH,
            height: SPLIT_BILL_IMAGE.RIGHT_LOGO_HEIGHT,
            placement: "beforeTitle",
        };
    }

    return undefined;
};

const resolveCta = (state: ISplitPayWidgetActiveState): ISplitBillWidgetCtaConfig | undefined => {
    if ("buttonText" in state && state.buttonText) {
        return {
            text: state.buttonText,
            iconName: state.buttonIcon as IconName,
            fillColor: "primary",
            strokeColor: "primary",
        };
    }

    return undefined;
};

export const getActiveSplitBillWidgetStateKey = (
    getCurrentStateFromApi: string,
    splitPayWidget: ISplitPayWidget,
    splitBillSuccess?: boolean,
    currentSplitWidgetState?: string,
): string | null => {
    if (splitBillSuccess) {
        const stateKey = currentSplitWidgetState || "default";
        return splitPayWidget.states?.[stateKey] ? stateKey : "default";
    }

    return getCurrentStateFromApi;
};

export const getSplitBillWidgetViewState = (
    getCurrentStateFromApi: string,
    splitPayWidget: ISplitPayWidget,
    splitBillSuccess?: boolean,
    currentSplitWidgetState?: string,
): ISplitBillWidgetViewState | null => {
    const stateKey = getActiveSplitBillWidgetStateKey(getCurrentStateFromApi,splitPayWidget, splitBillSuccess, currentSplitWidgetState);
    if (!stateKey) {
        return null;
    }

    const activeState = splitPayWidget.states[stateKey];
    return {
        title: activeState.title,
        subTitle: activeState.subTitle,
        lottieId: activeState.lottieId,
        widgetLottieId: activeState.widgetLottieId || "",
        titleBadge: resolveTitleBadge(activeState),
        cta: resolveCta(activeState),
        useSuccessSubtitleWidth: stateKey === "success",
        isClickable: activeState.isClickable ?? stateKey === "default",
    };
};

export const getSplitPayWidgetLottieId = (
    splitPayWidget?: ISplitPayWidget,
    splitBillSuccess?: boolean,
    currentSplitWidgetState?: string,
): string | undefined => {
    if (isEmpty(splitPayWidget) || !splitPayWidget?.states) {
        return undefined;
    }

    const getCurrentStateFromApi = getCurrentSplitWidgetStateFromApi(splitPayWidget);
    const widgetLottieId = getSplitBillWidgetViewState(
        getCurrentStateFromApi,
        splitPayWidget,
        splitBillSuccess,
        currentSplitWidgetState,
    )?.widgetLottieId;

    return widgetLottieId || undefined;
};
